import { Box, Button, Dialog, DialogContent, DialogTitle, Divider, Paper, SxProps, Typography, useTheme } from "@mui/material";
import { AccountsApi, MemberPayment, MemberPaymentItem, MemberPaymentMethod } from "../../../clients";
import { useCallback, useEffect, useState } from "react";
import { ConsClubTable } from "../../ConsClubTable/ConsClubTable";
import { getConfiguration } from "../../../clients/configuration";
import { useAppSelector } from "../../../redux/hooks";

interface MyAccountMembershipPaymentsProps {
    sx?: SxProps
    memberID: string
}

export const MyAccountMembershipPayments = ({ sx }: MyAccountMembershipPaymentsProps) => {

    const theme = useTheme()
    const currentUser = useAppSelector(state => state.currentUser.account)

    const [selectedPayment, setSelectedPayment] = useState<MemberPayment | null>(null)
    const [totalPaymentCount, setTotalPaymentCount] = useState<number>(0)
    const [paymentsPage, setPaymentsPage] = useState<number>(0)
    const [paymentsPageSize, setPaymentsPageSize] = useState<number>(25)
    const [payments, setPayments] = useState<MemberPayment[]>([])
    const [orderKey, setOrderKey] = useState<string>('payment_date')
    const [orderDirection, setOrderDirection] = useState<'ASC' | 'DESC'>('ASC')

    const loadPayments = useCallback(async () => {
        const config = await getConfiguration()
        const accountsApi = new AccountsApi(config)
        const payments = await accountsApi.getAccountMembershipPayments({
            accountID: currentUser?.id || '',
            pageNumber: paymentsPage,
            pageSize: paymentsPageSize,
            order: [{ key: orderKey, order: orderDirection }]
        })
        return payments
    }, [currentUser, paymentsPage, paymentsPageSize, orderKey, orderDirection])

    const handleViewPayment = (payment: MemberPayment) => {
        setSelectedPayment(payment)
    }

    const handleOrderDirectionChange = (direction: 'asc' | 'desc') => {
        setOrderDirection(direction.toUpperCase() as 'ASC' | 'DESC')
    }

    const mapPaymentItemToFriendlyName = (paymentItem: MemberPaymentItem) => {
        switch (paymentItem) {
            case MemberPaymentItem.Dues:
                return 'Dues'
            case MemberPaymentItem.GolfCartFee:
                return 'Golf Cart Fee'
            case MemberPaymentItem.Campsite:
                return 'Campsite Fee'
            case MemberPaymentItem.Other:
                return 'Other'
            default:
                return 'Unknown'
        }
    }

    useEffect(() => {
        loadPayments().then(payments => {
            setPayments(payments.data)
            setTotalPaymentCount(payments.metadata.totalCount)
        })
    }, [loadPayments])

    return (
        <Paper sx={{ ...sx, border: '1px solid', borderColor: theme.palette.secondary.main, borderRadius: '3px', p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography>Payments</Typography>
            </Box>
            <ViewMemberPaymentDialog
                open={selectedPayment !== null}
                onClose={() => setSelectedPayment(null)}
                payment={selectedPayment}
            />
            <ConsClubTable
                headers={[
                    { key: 'payment_date', label: 'Payment Date', sortable: true },
                    { key: 'payment_item', label: 'Payment Item', sortable: true },
                    { key: 'payment_amount', label: 'Payment Amount', sortable: true },
                ]}
                tableLabel="Payments"
                totalCount={totalPaymentCount}
                rows={payments.map(x => {
                    return [
                        { key: 'payment_date', value: x },
                        { key: 'payment_item', value: x.paymentItem },
                        { key: 'payment_amount', value: x.paymentAmountCents },
                    ]
                })}
                columnRenderers={{
                    'payment_date': (payment: MemberPayment) => <Button variant='text' color='primary' onClick={() => handleViewPayment(payment)}><Typography>{payment.paymentDate.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}</Typography></Button>,
                    'payment_amount': (value: number) => <Typography>{(value / 100).toLocaleString(undefined, { style: 'currency', currency: 'USD' })}</Typography>,
                    'payment_item': (value: MemberPaymentItem) => <Typography>{mapPaymentItemToFriendlyName(value)}</Typography>,
                }}
                onSizeChange={setPaymentsPageSize}
                onPageChange={setPaymentsPage}
                onSortChange={setOrderKey}
                onSortDirectionChange={handleOrderDirectionChange}
            />
        </Paper>
    )
}

interface ViewMemberPaymentDialogProps {
    payment: MemberPayment
    open: boolean
    onClose: () => void
}

const ViewMemberPaymentDialog = ({ payment, open, onClose }: ViewMemberPaymentDialogProps) => {

    const mapPaymentItemToFriendlyName = (paymentItem: MemberPaymentItem) => {
        switch (paymentItem) {
            case MemberPaymentItem.Dues:
                return 'Dues'
            case MemberPaymentItem.GolfCartFee:
                return 'Golf Cart Fee'
            case MemberPaymentItem.Campsite:
                return 'Campsite Fee'
            case MemberPaymentItem.Other:
                return 'Other'
            default:
                return 'Unknown'
        }
    }

    const mapPaymentMethodToFriendlyName = (paymentMethod: MemberPaymentMethod) => {
        switch (paymentMethod) {
            case MemberPaymentMethod.Cash:
                return 'Cash'
            case MemberPaymentMethod.Check:
                return 'Check'
            case MemberPaymentMethod.CreditCard:
                return 'Credit Card'
            case MemberPaymentMethod.Paypal:
                return 'PayPal'
            default:
                return 'Unknown'
        }
    }

    const handleClose = () => {
        onClose()
    }

    if (!payment) {
        return null
    }

    return (
        <Dialog open={open} onClose={handleClose}>
            <DialogTitle sx={{ position: 'relative' }}>
                Payment Details
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }} width='500px'>
                    <Box sx={{ flex: '1 1 25%', mt: 2 }}>
                        <Typography variant='caption'>Payment Date</Typography>
                        <Typography>{payment.paymentDate.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}</Typography>
                    </Box>
                    <Box sx={{ flex: '1 1 25%', mt: 2 }}>
                        <Typography variant='caption'>Payment Item</Typography>
                        <Typography>{mapPaymentItemToFriendlyName(payment.paymentItem)}</Typography>
                    </Box>
                    <Box sx={{ flex: '1 1 25%', mt: 2 }}>
                        <Typography variant='caption'>Payment Amount</Typography>
                        <Typography>{(payment.paymentAmountCents / 100).toLocaleString(undefined, { style: 'currency', currency: 'USD' })}</Typography>
                    </Box>
                    <Box sx={{ flex: '1 1 25%', mt: 2 }}>
                        <Typography variant='caption'>Payment Method</Typography>
                        <Typography>{mapPaymentMethodToFriendlyName(payment.paymentMethod)}</Typography>
                    </Box>
                    {
                        payment.paymentReference && (
                            <Box sx={{ flex: '1 1 25%', mt: 2 }}>
                                <Typography variant='caption'>Payment Reference</Typography>
                                <Typography>{payment.paymentReference}</Typography>
                            </Box>
                        )
                    }
                    {
                        payment.notes && (
                            <Box sx={{ flex: '1 1 100%', mt: 2 }}>
                                <Typography variant='caption'>Notes</Typography>
                                {payment.notes.split('\n').map((note, index) => <Typography key={index}>{note}</Typography>)}
                            </Box>
                        )
                    }
                    <Box sx={{ flex: '1 1 100%' }}>
                        <Divider />
                    </Box>
                    {
                        payment.refundDate && (
                            <Box sx={{ flex: '1 1 33%', mt: 1 }}>
                                <Typography variant='caption'>Refund Date</Typography>
                                <Typography>{payment.refundDate.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}</Typography>
                            </Box>
                        )
                    }
                    {
                        payment.refundAmountCents && (
                            <Box sx={{ flex: '1 1 33%', mt: 1 }}>
                                <Typography variant='caption'>Refund Amount</Typography>
                                <Typography>{(payment.refundAmountCents / 100).toLocaleString(undefined, { style: 'currency', currency: 'USD' })}</Typography>
                            </Box>
                        )
                    }
                    {
                        payment.refundReason && (
                            <Box sx={{ flex: '1 1 100%', mt: 2 }}>
                                <Typography variant='caption'>Refund Reason</Typography>
                                {payment.refundReason.split('\n').map((note, index) => <Typography key={index}>{note}</Typography>)}
                            </Box>
                        )
                    }

                </Box>
            </DialogContent>
        </Dialog>
    )
}